import React, { useState, useCallback, useEffect } from 'react';
import { Input } from '@/components/atoms/Input';
import { Button } from '@/components/atoms/Button';
import { Select } from '@/components/atoms/Select';
import { motion, AnimatePresence } from 'motion/react';
import { trackFormSubmit, trackEvent } from '@/lib/analytics';

interface FormData {
    name: string;
    company: string;
    email: string;
    countryCode: string;
    phoneNumber: string;
}

interface ContactFormProps {
    onClose: () => void;
}

const countryCodes = [
    { code: '+44', country: 'UK' },
    { code: '+353', country: 'IE' },
    { code: '+1', country: 'US' },
    { code: '+61', country: 'AU' },
    { code: '+64', country: 'NZ' },
    { code: '+33', country: 'FR' },
    { code: '+49', country: 'DE' },
];

// Animation variants
const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
        opacity: 1,
        transition: { duration: 0.3 }
    },
    exit: { 
        opacity: 0,
        transition: { duration: 0.2 }
    },
};

const modalVariants = {
    hidden: { opacity: 0, y: -20, scale: 0.98 },
    visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: {
            type: "spring" as const,
            damping: 22,
            stiffness: 180,
        }
    },
    exit: { 
        opacity: 0, 
        y: -10, 
        scale: 0.98,
        transition: { duration: 0.25 }
    },
};

const formFieldVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: 0.15 + i * 0.08,
            duration: 0.4,
            ease: [0.25, 0.1, 0.25, 1] as const,
        },
    }),
};

const successVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
        opacity: 1, 
        scale: 1,
        transition: {
            type: "spring" as const,
            damping: 20,
            stiffness: 300,
        }
    },
};

const ContactForm: React.FC<ContactFormProps> = ({ onClose }) => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        company: '',
        email: '',
        countryCode: '+44',
        phoneNumber: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isClosing, setIsClosing] = useState(false);

    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(onClose, 200);
    }, [onClose]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isSubmitting) {
                handleClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [handleClose, isSubmitting]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);
        
        try {
            const submissionData = {
                fromAddress: formData.email,
                name: formData.name,
                company: formData.company,
                email: formData.email,
                phoneNumber: `${formData.countryCode} ${formData.phoneNumber}`,
                message: `New contact request from ${formData.name} at ${formData.company}. Phone: ${formData.countryCode} ${formData.phoneNumber}`
            };
            
            const response = await fetch('https://crossmoore-contact-function.azurewebsites.net/api/SendEmail?code=0txdCBJ2wkY8V0nFBVFOS2KsvHOfZg7Pb1niE82Q9CXJAzFu7eCGpQ%3D%3D', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submissionData),
            });
            
            const data = await response.json();
            
            if (data.success) {
                setSubmitSuccess(true);
                trackFormSubmit('contact_form', true);
                trackEvent('lead', { form_name: 'contact_form' });
                setTimeout(() => {
                    handleClose();
                }, 2500);
            } else {
                throw new Error(data.message || 'Unknown error occurred');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitError(error instanceof Error ? error.message : 'An error occurred. Please try again.');
            trackFormSubmit('contact_form', false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {!isClosing && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50"
                        variants={backdropVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={!isSubmitting ? handleClose : undefined}
                    />
                    
                    {/* Modal Container */}
                    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 md:pt-24 overflow-y-auto">
                        <motion.div
                            className="w-full max-w-md"
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Card */}
                            <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
                                {/* Header */}
                                <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h2 className="text-2xl font-medium text-gray-900">
                                                Let's Talk
                                            </h2>
                                            <p className="text-gray-500 mt-1 text-sm">
                                                Tell us about your monitoring needs
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleClose}
                                            disabled={isSubmitting}
                                            className="p-2 -mr-2 -mt-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                                            aria-label="Close"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="px-6 py-5">
                                    <AnimatePresence mode="wait">
                                        {submitSuccess ? (
                                            <motion.div
                                                key="success"
                                                variants={successVariants}
                                                initial="hidden"
                                                animate="visible"
                                                className="text-center py-8"
                                            >
                                                <motion.div 
                                                    className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ type: "spring" as const, damping: 15, stiffness: 300, delay: 0.1 }}
                                                >
                                                    <motion.svg 
                                                        className="w-8 h-8 text-emerald-600" 
                                                        fill="none" 
                                                        viewBox="0 0 24 24" 
                                                        stroke="currentColor"
                                                        initial={{ pathLength: 0 }}
                                                        animate={{ pathLength: 1 }}
                                                        transition={{ duration: 0.5, delay: 0.2 }}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                    </motion.svg>
                                                </motion.div>
                                                <h3 className="text-xl font-medium text-gray-900 mb-2">Thank you!</h3>
                                                <p className="text-gray-500">We've received your message and will be in touch soon.</p>
                                            </motion.div>
                                        ) : (
                                            <motion.form 
                                                key="form"
                                                onSubmit={handleSubmit} 
                                                className="space-y-4"
                                            >
                                                {/* Name Field */}
                                                <motion.div
                                                    custom={0}
                                                    variants={formFieldVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                >
                                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                        Name
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        required
                                                        value={formData.name}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            setFormData({ ...formData, name: e.target.value })}
                                                        className="w-full h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                        placeholder="Your name"
                                                        disabled={isSubmitting}
                                                    />
                                                </motion.div>

                                                {/* Company Field */}
                                                <motion.div
                                                    custom={1}
                                                    variants={formFieldVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                >
                                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                        Company
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        required
                                                        value={formData.company}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            setFormData({ ...formData, company: e.target.value })}
                                                        className="w-full h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                        placeholder="Your company name"
                                                        disabled={isSubmitting}
                                                    />
                                                </motion.div>

                                                {/* Email Field */}
                                                <motion.div
                                                    custom={2}
                                                    variants={formFieldVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                >
                                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                        Email
                                                    </label>
                                                    <Input
                                                        type="email"
                                                        required
                                                        value={formData.email}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            setFormData({ ...formData, email: e.target.value })}
                                                        className="w-full h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                        placeholder="your.email@company.com"
                                                        disabled={isSubmitting}
                                                    />
                                                </motion.div>

                                                {/* Phone Field */}
                                                <motion.div
                                                    custom={3}
                                                    variants={formFieldVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                >
                                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                        Phone Number
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <Select
                                                            value={formData.countryCode}
                                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                                                setFormData({ ...formData, countryCode: e.target.value })}
                                                            className="w-24 h-11 px-3 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow text-sm"
                                                            disabled={isSubmitting}
                                                        >
                                                            {countryCodes.map(({ code, country }) => (
                                                                <option key={code} value={code}>
                                                                    {code} {country}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                        <Input
                                                            type="tel"
                                                            required
                                                            value={formData.phoneNumber}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                setFormData({ ...formData, phoneNumber: e.target.value })}
                                                            className="flex-1 h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                            placeholder="Phone number"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </motion.div>

                                                {/* Error Message */}
                                                <AnimatePresence>
                                                    {submitError && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -10, height: 0 }}
                                                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                                                            exit={{ opacity: 0, y: -10, height: 0 }}
                                                            className="flex items-start gap-3 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-100"
                                                        >
                                                            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <span>{submitError}</span>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>

                                                {/* Submit Button */}
                                                <motion.div
                                                    custom={4}
                                                    variants={formFieldVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                    className="pt-2"
                                                >
                                                    <Button
                                                        type="submit"
                                                        className="w-full h-11 bg-gray-900 text-white hover:bg-gray-800 rounded-lg font-medium transition-all hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? (
                                                            <span className="flex items-center justify-center gap-2">
                                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                Sending...
                                                            </span>
                                                        ) : (
                                                            'Send Message'
                                                        )}
                                                    </Button>
                                                </motion.div>

                                                {/* Privacy Note */}
                                                <motion.p
                                                    custom={5}
                                                    variants={formFieldVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                    className="text-xs text-gray-400 text-center pt-2"
                                                >
                                                    We'll never share your information with third parties.
                                                </motion.p>
                                            </motion.form>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ContactForm; 
